import React, { useState } from 'react';
import cn from 'classnames';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import Button, { ButtonVariantType } from '@dealroadshow/uikit-button';
import { useRestrictedListingContext } from '../../../../contexts/RestrictedListingContext';
import { useAccessRequestContext } from '../../../../contexts/AccessRequestContext';
import stylesForSet from '../../set.module.scss';
import spacesStyles from '@dealroadshow/uikit-styles/helpers/spaces.module.scss';
import styles from './accessRequestStatuses.module.scss';
import { IDataroomListItem } from '../../../../domain/vo/DataroomListItem';
import AllowedActions from '../../../Datarooms/AllowedActions';
import { ICellProps } from '@dealroadshow/uikit-table/DataTable';

interface IProps {
  cellProps: ICellProps<IDataroomListItem>,
  withSessionCheck?: boolean,
  isRestricted?: boolean,
}

const AccessRequestStatuses = ({
  cellProps,
  withSessionCheck,
  isRestricted,
}: IProps) => {
  const dataroom = cellProps.row;
  const {
    isPending,
    isDenied,
    isFollowing,
  } = dataroom.accessRequest!;
  const isRequestAccess = !isPending && !isDenied && !isFollowing;
  const { canFollow } = dataroom.allowedActions;
  const [isVisibleSpinner, setIsVisibleSpinner] = useState(false);

  const { getCollection } = useRestrictedListingContext();

  const {
    sendFollowRequest,
    requestAccess,
  } = useAccessRequestContext();

  const handleRequest = async () => {
    setIsVisibleSpinner(true);

    const requestedDataroom = {
      id: dataroom.id,
      name: dataroom.displayName,
    };

    if (canFollow) {
      await sendFollowRequest(requestedDataroom, withSessionCheck)
      .then(getCollection);
    } else {
      await requestAccess(requestedDataroom, withSessionCheck);
    }

    setIsVisibleSpinner(false);
  };

  if (isFollowing) {
    return (
      <AllowedActions
        cellProps={ cellProps }
        onSuccessUnfollowCallback={ getCollection }
        isRestricted={ isRestricted }
      />
    );
  }

  return (
    <div>
      <div className={ cn(stylesForSet.dataroomStatuses, styles.accessRequestStatuses) }>
        <span className={ stylesForSet.mobileColStatuses }> Status </span>
        {
          (canFollow || isRequestAccess) && !isVisibleSpinner && (
            isRestricted ? (
              <Button
                type="button"
                variant={ ButtonVariantType.action }
                className={ cn(spacesStyles.mrn, {
                  [styles.requestAccessButton]: !canFollow,
                  [styles.followButton]: canFollow,
                }) }
                disabled
                title={ canFollow ? 'Follow' : 'Request Access' }
                dataTest="followOrAccessRequestButton"
              />
            ) : (
              <Button
                type="button"
                variant={ ButtonVariantType.action }
                className={ cn(spacesStyles.mrn, {
                  [styles.requestAccessButton]: !canFollow,
                  [styles.followButton]: canFollow,
                }) }
                onClick={ handleRequest }
                title={ canFollow ? 'Follow' : 'Request Access' }
                dataTest="followOrAccessRequestButton"
              />
            )
          )
        }
        {
          !canFollow && !isRequestAccess && (
            <div
              className={ cn(styles.requestStatus, {
                [styles.pending]: isPending,
                [styles.denied]: isDenied,
              }) }
            >
              { isPending && 'Access Pending' }
              { isDenied && 'Access Denied' }
            </div>
          )
        }
        {
          isVisibleSpinner && (
            <div
              className={ cn({
                [styles.accessRequestSpinner]: !canFollow,
                [styles.followSpinner]: canFollow,
              }) }
            >
              <Spinner className={ styles.spinner } />
            </div>
          )
        }
      </div>
    </div>
  );
};

export default AccessRequestStatuses;
